import React from 'react';
import { LucideIcon } from 'lucide-react';

interface ActionCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  iconColor?: string;
  onClick?: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
  title,
  description,
  icon: Icon,
  iconColor = 'text-blue-600',
  onClick
}) => {
  return (
    <button
      onClick={onClick}
      className="group bg-white rounded-xl shadow-soft border border-gray-100 p-6 text-left hover:shadow-strong hover:-translate-y-1 transition-all duration-300 w-full relative overflow-hidden"
    >
      {/* Subtle Sand Drawing Pattern Background */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="actionCardPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              {/* Micro spiral */}
              <path d="M 50,50 Q 55,45 60,50 Q 65,55 60,60 Q 55,65 50,60 Q 45,55 50,50" 
                    stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.4"/>
              
              {/* Small dots */}
              <circle cx="20" cy="30" r="1.5" fill="currentColor" opacity="0.3"/>
              <circle cx="80" cy="70" r="1.5" fill="currentColor" opacity="0.3"/>
              <circle cx="30" cy="80" r="1" fill="currentColor" opacity="0.2"/>
              <circle cx="70" cy="20" r="1" fill="currentColor" opacity="0.2"/>
              
              {/* Tiny wave strokes */}
              <path d="M 10,50 Q 20,45 30,50" stroke="currentColor" strokeWidth="0.6" fill="none" opacity="0.3"/>
              <path d="M 70,80 Q 80,75 90,80" stroke="currentColor" strokeWidth="0.6" fill="none" opacity="0.3"/>
              
              {/* Triangle accent */}
              <path d="M 85,15 L 90,25 L 80,25 Z" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.2"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#actionCardPattern)"/>
        </svg>
      </div>
      
      {/* Card Content */}
      <div className="relative z-10">
        <div className={`${iconColor} mb-4 transition-transform group-hover:scale-110 duration-300`}>
          <Icon className="w-8 h-8" />
        </div>
        <h3 className="font-semibold text-gray-900 mb-2 text-lg">
          {title}
        </h3>
        <p className="text-sm text-gray-600">
          {description}
        </p>
        <div className="mt-4 flex items-center text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
          <span>Get started</span>
          <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </button>
  );
};

export default ActionCard;
